import * as React from "react"
import { useDispatch, useSelector } from "react-redux";
import { Toaster } from "react-hot-toast";
import { States } from "../../interfaces/states";
import { TOGGLE_NOTIFICATION } from "../../constants";
import { showNotification } from "./Notification/notify";

/**
 * Bridges the Redux notification slice to react-hot-toast.
 *
 * Existing code keeps calling `dispatch(toggleNotification({...}))` exactly as
 * before — this component watches that slice and raises a toast whenever a new
 * notification is toggled on, then resets the flag so an identical message can
 * be raised again later.
 *
 * Mounted once, in App.tsx.
 */
const NotificationHook: React.FunctionComponent = (): React.ReactElement<void> => {
    const notificationState = useSelector((state: States) => state.notification);
    const dispatch = useDispatch();
    const notification: any = notificationState?.notification;

    // Guard against re-firing for the same object on unrelated re-renders.
    const lastFired = React.useRef<any>(null);

    React.useEffect(() => {
        if (!notification?.toggle) return;
        if (lastFired.current === notification) return;
        lastFired.current = notification;

        showNotification({
            ...notification,
            onDismiss: () => notification?.onDismiss && notification.onDismiss(),
        });

        // Reset the flag so the same notification can be raised again later.
        dispatch({
            type: TOGGLE_NOTIFICATION,
            notification: { ...notification, toggle: false },
        });
    }, [notification, dispatch]);

    return (
        <Toaster
            position="top-right"
            gutter={10}
            containerStyle={{ zIndex: 999999 }}
            toastOptions={{ duration: 4000 }}
        />
    );
}


export default NotificationHook;
